import { buildAuthedHeaders } from '@/lib/api-client'
import { env } from '@/lib/env'

export type WikiOperation = 'create' | 'ingest' | 'query' | 'lint'

export type WikiStreamEvent =
  | { type: 'status'; phase: string; detail?: string }
  | { type: 'token'; content: string }
  | { type: 'page_written'; path: string; concept: string }
  | {
      type: 'done'
      operation: WikiOperation
      pages_written?: string[]
      lint_report_path?: string
      query_answer?: string
    }
  | { type: 'error'; message: string }

export interface RunWikiOptions {
  operation: WikiOperation
  topic?: string
  args?: string
  premium?: boolean
  sessionId?: string
  signal?: AbortSignal
  onEvent: (event: WikiStreamEvent) => void
}

export async function streamWiki(opts: RunWikiOptions): Promise<void> {
  const baseUrl = env.VITE_API_URL.replace(/\/+$/, '')

  const response = await fetch(`${baseUrl}/api/wiki`, {
    method: 'POST',
    headers: await buildAuthedHeaders({
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    }),
    body: JSON.stringify({
      operation: opts.operation,
      topic: opts.topic,
      args: opts.args,
      premium: opts.premium,
      session_id: opts.sessionId,
    }),
    signal: opts.signal,
  })

  if (!response.ok) {
    let message = response.statusText
    try {
      const body = (await response.json()) as { error?: { message?: string } }
      message = body.error?.message ?? message
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }

  if (!response.body) throw new Error('Keine Stream-Antwort')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      let separatorIdx = buffer.indexOf('\n\n')
      while (separatorIdx !== -1) {
        const rawEvent = buffer.slice(0, separatorIdx)
        buffer = buffer.slice(separatorIdx + 2)

        for (const line of rawEvent.split('\n')) {
          if (!line.startsWith('data:')) continue
          const payload = line.slice(5).trim()
          if (!payload) continue
          let parsed: WikiStreamEvent
          try {
            parsed = JSON.parse(payload) as WikiStreamEvent
          } catch (err) {
            console.error('SSE parse error', err, payload)
            continue
          }
          opts.onEvent(parsed)
        }

        separatorIdx = buffer.indexOf('\n\n')
      }
    }
  } finally {
    reader.releaseLock()
  }
}
